import { View } from 'react-native';

import { Heading, Icon, IconButton } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { StandbyScreenHeaderProps } from './types';

/**
 * Standby flow header: a standalone rounded-square back button on its own
 * line with the screen title (Heading L) beneath it — the shared header for
 * the Select stylist / Select service screens, deliberately NOT the inline
 * `ScreenHeader` (back arrow beside a small title), which does not match the
 * standby designs.
 *
 * The close-only variant (entries list) is a different Figma layout — title
 * and close button share one row — so it's handled as its own early return
 * rather than folded into the back-button markup below.
 */
const StandbyScreenHeader = ({
  title,
  onBack,
  onClose,
  testID = 'standby-screen-header',
}: StandbyScreenHeaderProps) => {
  const { t } = useScreenTranslations('shared');

  if (onClose && !onBack) {
    return (
      <View style={styles.closeRow} testID={testID}>
        <Heading
          variant="l"
          accessibilityRole="header"
          style={styles.closeTitle}
        >
          {title}
        </Heading>
        <IconButton
          size="l"
          state="hover"
          icon={<Icon name="close" />}
          onPress={onClose}
          accessibilityLabel={t('screenHeader.closeButton.accessibilityLabel')}
          accessibilityHint={t('screenHeader.closeButton.accessibilityHint')}
          testID="standby-screen-header-close"
        />
      </View>
    );
  }

  return (
    <View style={styles.container} testID={testID}>
      {onBack || onClose ? (
        <View style={styles.controlsRow}>
          {onBack ? (
            <IconButton
              size="xl"
              // The Nav Bar back button uses the DS "Button Icon" component's
              // Hover state as its permanent look (a filled chip, not a
              // ghost) — matches DetailsStep/OtpStep. IconButton owns the
              // icon size/color.
              state="hover"
              icon={<Icon name="chevronLeft" />}
              onPress={onBack}
              accessibilityLabel={t(
                'screenHeader.backButton.accessibilityLabel',
              )}
              accessibilityHint={t('screenHeader.backButton.accessibilityHint')}
              testID="standby-screen-header-back"
            />
          ) : null}
          {onClose ? (
            // Same rounded-square chip as OtpStep's desktop drawer close
            // control, reused here for the entries list's X-to-dismiss.
            <IconButton
              size="xl"
              state="hover"
              icon={<Icon name="close" />}
              onPress={onClose}
              accessibilityLabel={t(
                'screenHeader.closeButton.accessibilityLabel',
              )}
              accessibilityHint={t(
                'screenHeader.closeButton.accessibilityHint',
              )}
              testID="standby-screen-header-close"
            />
          ) : null}
        </View>
      ) : null}
      <Heading variant="l" accessibilityRole="header">
        {title}
      </Heading>
    </View>
  );
};

StandbyScreenHeader.displayName = 'StandbyScreenHeader';
export { StandbyScreenHeader };
